// One section of a template — the order's checks, or the checks at one kind of
// stop — and the checkpoint rows inside it. A row shows what is decided most
// often (what, how, who, when); the rest is behind the chevron.
import type { TaskTypeValue } from '@my/tms-api';
import { cn } from '@my/ui';
import { ArrowDown, ArrowUp, ChevronDown, ClipboardList, Lock, MapPin, Plus, Trash2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { taskTypeLabel } from '@/features/orders/lib/taskTypeConfig';
import {
  ALL_TASK_TYPES,
  DUE_LABEL,
  DUE_ORDER,
  KIND_ORDER,
  KINDS,
  MOMENT_LABEL,
  MOMENT_ORDER,
  WRITE_BACK_LABEL,
  WRITE_BACK_SHORT,
  describeRequired,
  describeSignOff,
  newCheckpoint,
  type Checkpoint,
  type CheckpointConfig,
  type CheckpointKind,
  type Due,
  type Moment,
  type Section,
  type SectionScope,
  type WriteBack,
} from '../lib/model';
import { RoleToggles } from './roles';

const chip =
  'h-6 rounded border border-slate-200 bg-white px-1.5 text-[11px] text-slate-700 hover:border-slate-300 focus:border-sky-300 focus:outline-none';
const input =
  'h-7 w-full rounded border border-slate-300 bg-white px-2 text-xs text-slate-800 focus:border-sky-400 focus:outline-none';

export function CheckpointSection({
  section,
  onChange,
  onRemove,
}: {
  section: Section;
  onChange: (next: Section) => void;
  /** Task sections only — every template keeps its order section. */
  onRemove?: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const isOrder = section.scope === 'order';

  const setCheckpoints = (checkpoints: Checkpoint[]) => onChange({ ...section, checkpoints });
  const patch = (id: string, changes: Partial<Checkpoint>) =>
    setCheckpoints(section.checkpoints.map((c) => (c.id === id ? { ...c, ...changes } : c)));
  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    if (target < 0 || target >= section.checkpoints.length) return;
    const next = [...section.checkpoints];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    setCheckpoints(next);
  };
  const add = () => {
    const c = newCheckpoint(section.scope);
    setCheckpoints([...section.checkpoints, c]);
    setOpenId(c.id);
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 px-4 py-2">
        {isOrder ? (
          <>
            <ClipboardList className="h-4 w-4 text-slate-400" />
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Order</h3>
            <span className="text-[11px] text-slate-400">
              Once per order — the planner's checks, and on-site ones done when the job is accepted.
            </span>
          </>
        ) : (
          <>
            <MapPin className="h-4 w-4 text-slate-400" />
            <input
              value={section.name}
              onChange={(e) => onChange({ ...section, name: e.target.value })}
              placeholder="Section name"
              aria-label="Section name"
              className="w-32 rounded border border-transparent px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:border-slate-200 focus:border-sky-300 focus:outline-none"
            />
            <TaskTypePicker value={section.taskTypes} onChange={(taskTypes) => onChange({ ...section, taskTypes })} />
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                aria-label="Remove section"
                className="ml-auto text-slate-300 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </header>

      {!isOrder && section.taskTypes.length === 0 && (
        <p className="border-b border-amber-100 bg-amber-50 px-4 py-1.5 text-[11px] text-amber-800">
          No task type picked — this section never runs until one is.
        </p>
      )}

      {section.checkpoints.length === 0 ? (
        <p className="px-4 py-4 text-center text-xs text-slate-400">
          {isOrder ? 'No order checkpoints.' : 'Nothing to check at these stops yet.'}
        </p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {section.checkpoints.map((c, i) => (
            <CheckpointRow
              key={c.id}
              checkpoint={c}
              index={i}
              count={section.checkpoints.length}
              scope={section.scope}
              open={openId === c.id}
              onToggleOpen={() => setOpenId((id) => (id === c.id ? null : c.id))}
              onPatch={(changes) => patch(c.id, changes)}
              onMove={(by) => move(i, by)}
              onRemove={() => setCheckpoints(section.checkpoints.filter((x) => x.id !== c.id))}
            />
          ))}
        </ol>
      )}

      <div className="border-t border-slate-100 px-4 py-1.5">
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900"
        >
          <Plus className="h-3.5 w-3.5" /> Add checkpoint
        </button>
      </div>
    </section>
  );
}

function TaskTypePicker({ value, onChange }: { value: TaskTypeValue[]; onChange: (next: TaskTypeValue[]) => void }) {
  const available = ALL_TASK_TYPES.filter((t) => !value.includes(t));
  return (
    <span className="flex flex-wrap items-center gap-1">
      {value.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 py-0.5 pl-2 pr-1 text-[11px] text-slate-700"
        >
          {taskTypeLabel(t)}
          <button
            type="button"
            aria-label={`Remove ${taskTypeLabel(t)}`}
            onClick={() => onChange(value.filter((x) => x !== t))}
            className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) onChange([...value, e.target.value as TaskTypeValue]);
          }}
          aria-label="Add a task type"
          className={cn(chip, 'text-slate-500')}
        >
          <option value="">+ task type</option>
          {available.map((t) => (
            <option key={t} value={t}>
              {taskTypeLabel(t)}
            </option>
          ))}
        </select>
      )}
    </span>
  );
}

function CheckpointRow({
  checkpoint: c,
  index,
  count,
  scope,
  open,
  onToggleOpen,
  onPatch,
  onMove,
  onRemove,
}: {
  checkpoint: Checkpoint;
  index: number;
  count: number;
  scope: SectionScope;
  open: boolean;
  onToggleOpen: () => void;
  onPatch: (changes: Partial<Checkpoint>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const meta = KINDS[c.kind];

  // A kind that only works on site takes the planner off (falling back to
  // whoever does the stop), and drops an update this kind can't make.
  const changeKind = (kind: CheckpointKind) => {
    const next = KINDS[kind];
    let roles = c.roles;
    if (next.onSiteOnly && roles.includes('planner')) {
      roles = roles.filter((r) => r !== 'planner');
      if (roles.length === 0) roles = ['driver', 'sub'];
    }
    onPatch({ kind, roles, writeBack: next.writeBacks.includes(c.writeBack) ? c.writeBack : 'none' });
  };

  return (
    <li className="group px-4 py-2.5">
      <div className="flex items-start gap-2">
        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] font-semibold text-slate-600">
          {index + 1}
        </span>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              value={c.label}
              onChange={(e) => onPatch({ label: e.target.value })}
              placeholder="What has to be checked?"
              aria-label="Checkpoint"
              className="min-w-0 flex-1 rounded border border-transparent px-1 py-0.5 text-sm text-slate-800 placeholder:text-slate-300 hover:border-slate-200 focus:border-sky-300 focus:outline-none"
            />
            {!c.waivable && (
              <span title="Cannot be waived on an order" className="shrink-0 text-slate-400">
                <Lock className="h-3.5 w-3.5" />
              </span>
            )}
            {/* Light red, like a required form field. */}
            <button
              type="button"
              onClick={() => onPatch({ required: !c.required })}
              title={
                c.required
                  ? `${describeRequired(c.roles, scope)}. Click to make it optional.`
                  : 'Click to make it required'
              }
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1',
                c.required ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-white text-slate-500 ring-slate-200',
              )}
            >
              {c.required ? '✱ Required' : 'Optional'}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pl-1">
            <select
              value={c.kind}
              onChange={(e) => changeKind(e.target.value as CheckpointKind)}
              aria-label="How it is checked"
              className={chip}
            >
              {KIND_ORDER.map((k) => (
                <option key={k} value={k}>
                  {KINDS[k].label}
                </option>
              ))}
            </select>
            <RoleToggles roles={c.roles} onChange={(roles) => onPatch({ roles })} plannerDisabled={meta.onSiteOnly} />
            <span className="text-[10px] text-slate-400">{describeSignOff(c.roles, scope)}</span>
            <span className="mx-0.5 h-3 w-px bg-slate-200" />
            {scope === 'order' ? (
              <select
                value={c.due}
                onChange={(e) => onPatch({ due: e.target.value as Due })}
                aria-label="Must be done"
                className={chip}
              >
                {DUE_ORDER.map((d) => (
                  <option key={d} value={d}>
                    {DUE_LABEL[d]}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={c.moment}
                onChange={(e) => onPatch({ moment: e.target.value as Moment })}
                aria-label="When at the stop"
                className={chip}
              >
                {MOMENT_ORDER.map((m) => (
                  <option key={m} value={m}>
                    {MOMENT_LABEL[m]}
                  </option>
                ))}
              </select>
            )}
            {c.writeBack !== 'none' && (
              <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700">
                Updates {WRITE_BACK_SHORT[c.writeBack]}
              </span>
            )}
          </div>

          {open && <CheckpointDetails checkpoint={c} onPatch={onPatch} />}
        </div>

        <div className="flex shrink-0 items-center">
          <IconButton label="Move up" disabled={index === 0} onClick={() => onMove(-1)} hoverOnly>
            <ArrowUp className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label="Move down" disabled={index === count - 1} onClick={() => onMove(1)} hoverOnly>
            <ArrowDown className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label={open ? 'Fewer settings' : 'More settings'} onClick={onToggleOpen}>
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
          </IconButton>
          <IconButton label="Remove checkpoint" onClick={onRemove} hoverOnly danger>
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>
    </li>
  );
}

function CheckpointDetails({
  checkpoint: c,
  onPatch,
}: {
  checkpoint: Checkpoint;
  onPatch: (changes: Partial<Checkpoint>) => void;
}) {
  const meta = KINDS[c.kind];
  const setConfig = (changes: Partial<CheckpointConfig>) => onPatch({ config: { ...c.config, ...changes } });

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 rounded-md border border-slate-100 bg-slate-50 p-3 sm:grid-cols-2">
      <p className="text-[11px] text-slate-500 sm:col-span-2">{meta.detail}</p>

      <Field label="Help text" className="sm:col-span-2">
        <input
          value={c.hint}
          onChange={(e) => onPatch({ hint: e.target.value })}
          placeholder="Shown under the checkpoint on the phone and the order"
          className={input}
        />
      </Field>

      <KindSettings checkpoint={c} setConfig={setConfig} />

      {/* Not a setting: nothing blocks for now, so a failure only ever warns. */}
      {meta.canFail && (
        <Field label="If it fails">
          <p className="py-1 text-xs text-amber-800">Warns the planner — the driver carries on</p>
        </Field>
      )}

      {meta.writeBacks.length > 0 && (
        <Field label="Updates the order">
          <select
            value={c.writeBack}
            onChange={(e) => onPatch({ writeBack: e.target.value as WriteBack })}
            className={input}
          >
            {(['none', ...meta.writeBacks] as WriteBack[]).map((w) => (
              <option key={w} value={w}>
                {WRITE_BACK_LABEL[w]}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="On an order">
        <label className="flex items-center gap-1.5 text-xs text-slate-700">
          <input type="checkbox" checked={c.waivable} onChange={(e) => onPatch({ waivable: e.target.checked })} />
          Can be waived, with a reason
        </label>
      </Field>
    </div>
  );
}

/** The settings only some kinds have. */
function KindSettings({
  checkpoint: c,
  setConfig,
}: {
  checkpoint: Checkpoint;
  setConfig: (changes: Partial<CheckpointConfig>) => void;
}) {
  const cfg = c.config;
  const num = (v: string, fallback: number) => (Number.isFinite(Number(v)) && v !== '' ? Number(v) : fallback);

  switch (c.kind) {
    case 'photo':
      return (
        <Field label="Photos needed">
          <input
            type="number"
            min={1}
            max={10}
            value={cfg.minPhotos}
            onChange={(e) => setConfig({ minPhotos: Math.max(1, num(e.target.value, 1)) })}
            className={cn(input, 'w-20')}
          />
        </Field>
      );
    case 'reading':
      return (
        <Field label="Unit">
          <input value={cfg.unit} onChange={(e) => setConfig({ unit: e.target.value })} className={cn(input, 'w-32')} />
        </Field>
      );
    case 'quantity':
      return (
        <>
          <Field label="Counted in">
            <input value={cfg.unit} onChange={(e) => setConfig({ unit: e.target.value })} className={cn(input, 'w-32')} />
          </Field>
          <Field label="Compare">
            <label className="flex items-center gap-1.5 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={cfg.compareToPlanned}
                onChange={(e) => setConfig({ compareToPlanned: e.target.checked })}
              />
              With the planned quantity
            </label>
          </Field>
        </>
      );
    case 'temperature':
      return (
        <Field label="Allowed range" className="sm:col-span-2">
          <div className="space-y-1 text-xs text-slate-700">
            <label className="flex flex-wrap items-center gap-1.5">
              <input type="radio" checked={cfg.tempRange === 'cargo'} onChange={() => setConfig({ tempRange: 'cargo' })} />
              From the cargo — set temperature ±
              <input
                type="number"
                min={0}
                max={20}
                value={cfg.toleranceC}
                disabled={cfg.tempRange !== 'cargo'}
                onChange={(e) => setConfig({ toleranceC: num(e.target.value, 0) })}
                className={cn(input, 'w-14')}
              />
              °C
            </label>
            <label className="flex flex-wrap items-center gap-1.5">
              <input type="radio" checked={cfg.tempRange === 'fixed'} onChange={() => setConfig({ tempRange: 'fixed' })} />
              Fixed —
              <input
                type="number"
                value={cfg.minC}
                disabled={cfg.tempRange !== 'fixed'}
                onChange={(e) => setConfig({ minC: num(e.target.value, 0) })}
                className={cn(input, 'w-14')}
              />
              to
              <input
                type="number"
                value={cfg.maxC}
                disabled={cfg.tempRange !== 'fixed'}
                onChange={(e) => setConfig({ maxC: num(e.target.value, 0) })}
                className={cn(input, 'w-14')}
              />
              °C
            </label>
          </div>
        </Field>
      );
    case 'identifier':
      return (
        <>
          <Field label="Number of">
            <select
              value={cfg.identifierOf}
              onChange={(e) => setConfig({ identifierOf: e.target.value as CheckpointConfig['identifierOf'] })}
              className={input}
            >
              <option value="bic">Container / tank (BIC)</option>
              <option value="seal">Seal</option>
              <option value="other">Other reference</option>
            </select>
          </Field>
          <Field label="Compare">
            <label className="flex items-center gap-1.5 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={cfg.compareToPlanned}
                onChange={(e) => setConfig({ compareToPlanned: e.target.checked })}
              />
              With the number on the order
            </label>
          </Field>
        </>
      );
    default:
      return null;
  }
}

function Field({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className}>
      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  hoverOnly,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Only shown while the row is hovered — keeps the list quiet. */
  hoverOnly?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30',
        danger ? 'hover:text-red-600' : 'hover:text-slate-700',
        hoverOnly && 'opacity-0 group-hover:opacity-100 focus:opacity-100',
      )}
    >
      {children}
    </button>
  );
}
